/* eslint-disable react/display-name */
/* eslint-disable max-len */
import { VoidCallback1 } from '@deqo/foundation';
import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import { useEffectOnce, useSessionStorage } from 'react-use';
import styled from 'styled-components';
import { Arr } from '../../../CodesForFoundation';
import { LFC } from '../../../Legacy';
import { Component } from '../../../concepts/objects';
import { PillEditor, PillEditorProps } from '../../../ui/PillEditor';
import { useResolve } from '../../Container';
import { BoardInfo } from '../../canvas/BoardInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { AnchoredScroller } from '../../common/AnchoredScroller';
import { Copy, Title } from '../../dashboard/elements/LayoutElements';
import { Popup } from '../../popups/Popup';
import { Box, Button, Colors, Menu, Select } from './../../../ui';
import { ExportAdapter, ExportAdapterLibrary } from './ExportAdapter';
import { ExportDimension, ExportDimensionType } from './ExportDimension';
import { ExportPreview } from './ExportPreview';
import { ExportConfiguration, ExportService, SerializedExportConfiguration } from './ExportService';
import { ExportSource, ExportSourceLibrary } from './ExportSource';
import { ExportTypes } from './ExportTypes';

const XScrollbars = Scrollbars as any;

const BlockFrame = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    position: relative;
`;

const Block = styled.div<{ grow?: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 1rem;
    flex: ${p => p.grow ? '1 1' : '0 0'} auto;

    & > ${Title} {
        flex: 0 0;
        height: 3rem;
        line-height: 3rem;
        color: ${Colors.text};
        font-weight: 400;
        font-size: 0.875rem;
        text-transform: uppercase;
        white-space: nowrap;

        margin: 0 1.5rem;

        &:first-child {
            /* flex: 0 0 6rem; */
            margin-left: 0;
        }
    }

    & button {
        min-width: 10rem;
    }
`;

const SourcePicker: LFC<{ config: ExportConfiguration, onPick: VoidCallback1<ExportSource> }> = ({ config, onPick }) => {

    const options = useResolve(ExportSourceLibrary).getAll().flatMap(x => x.get());
    const selected = options.find(x => x.id === config.source?.id);
    const onChange = useCallback((source: ExportSource) => onPick(source), [onPick]);

    return <Select width={14} selected={selected} items={options} onChange={onChange} renderItems={
        (items, selectedItem, onSelect) => items.map(tm =>
            <Select.MenuItem key={tm.id} disabled={!tm.canRead()} onClick={tm.canRead() ? onSelect(tm) as any : null}>
                <Select.MenuItemText>
                    {tm.label}
                    {tm.canRead() ? '' : ' (empty)'}
                </Select.MenuItemText>
                {tm === selectedItem && <Select.MenuItemCheck />}
            </Select.MenuItem>
        )
    } />;
};

const TypePicker: LFC<{ config: ExportConfiguration, onPick: VoidCallback1<string> }> = ({ config, onPick }) => {

    const stats = useMemo(() => !!config.source ? config.source.stats(ExportTypes.map(x => x.key)) : {}, [config]);
    const options = useMemo(() => ExportTypes.map(x => ({ ...x, count: stats[x.key] ?? 0 })), [stats]);
    const selected = ExportTypes.find(x => x.key === config.type);
    const onChange = useCallback((item: any) => onPick(item?.key ?? null), [onPick]);

    return <Select width={14} selected={selected} items={options} onChange={onChange} renderItems={
        (items, selectedItem, onSelect) => items.map(tm =>
            <Select.MenuItem key={tm.key} disabled={tm.count === 0} onClick={tm.count > 0 ? onSelect(tm) as any : null}>
                <Select.MenuItemText>
                    {tm.label} {`(${tm.count})`}
                </Select.MenuItemText>
                {tm === selectedItem && <Select.MenuItemCheck />}
            </Select.MenuItem>
        )
    } />;
};

type ExportDimensionOption = { group: string, default: boolean, type: ExportDimensionType };

const resolveDimensionOptions = (rows: Component[], adapters: ExportAdapter[], config: ExportConfiguration) => {
    const ignore = new Set(config.dimensions.map(x => x.id));
    return Arr
        .sortBy(adapters, 'priority')
        .flatMap(a => a.query(rows).map(
            t => ({ group: a.label, default: a.defaults, type: t })
        ))
        .filter(x => !ignore.has(x.type.id));
};

const createAddColumnMenu = (options: ExportDimensionOption[]): LFC<PillEditorProps<ExportDimension>> => {
    return ({ items, onUpdate }) => {

        const groups = useMemo(() =>
            Object.entries(Arr.groupBy(options, 'group'))
                .map(([title, list]) => [title, list.map(x => x.type)] as const),
            [options]);

        const [active, setActive] = useState(null);

        const onCreate = useCallback(() => {
            const inst = active.createInstance();
            onUpdate(items.filter(x => x.id !== inst.id).concat(inst));
        }, [active, onUpdate]);

        return <AnchoredScroller>
            <AnchoredScroller.Left>
                <Menu>
                    {groups.map(([title, list], i) =>
                        list.length && <Menu.Section key={title} first={!i} title={title}>
                            {list.map(x =>
                                <Menu.Item
                                    key={x.id}
                                    onClick={() => onCreate()}
                                    onMouseEnter={() => setActive(x)}>
                                    <Menu.ItemText>{x.label}</Menu.ItemText>
                                </Menu.Item>
                            )}
                        </Menu.Section>
                    )}
                </Menu>
            </AnchoredScroller.Left>
            <AnchoredScroller.Right>
                {active && <Box padding=".5">
                    <Title size="sm">{active.label}</Title>
                    <Copy>{active.description}</Copy>
                </Box>}
            </AnchoredScroller.Right>
        </AnchoredScroller>;
    };
};

const ColumnEditor: LFC<{ item: ExportDimension, onEdit: any }> = ({ item, onEdit }) => {
    const Editor = item.type.ui;
    return Editor ? <Editor item={item} onEdit={onEdit} /> : null;
};

type ConfigAction =
    | { set: '*', to: ExportConfiguration }
    | { set: 'source', to: ExportSource }
    | { set: 'type', to: string }
    | { set: 'dimensions', to: ExportDimension[] };

const configReducer = (sourceLib: ExportSourceLibrary, adapterLib: ExportAdapterLibrary, exporter: ExportService) =>
    (prev: ExportConfiguration, { set, to }: ConfigAction) => {
        let next = set === '*' ? (to as ExportConfiguration) : { ...prev, [set]: to };

        // Should we have any dimensions?  If not remove all
        if (!next.source || !next.type) {
            next = { ...next, dimensions: [] };
        }

        // Has our type changed?  If so, check if we have any invalid columns.
        // If we do, wipe them all (thats right, it feels weird an other way)
        if (next.type !== prev.type) {
            const rows = exporter.query(next);
            const pool = adapterLib.getAll().flatMap(a => a.query(rows));
            const idLookup = new Set(adapterLib.getAll().flatMap(a => a.query(rows)).map(x => x.id));
            if (!!next.dimensions.find(x => !idLookup.has(x.id))) {
                next = { ...next, dimensions: [] };
            }
        }

        // Should we select defaults?
        if (!!next.source && !!next.type && next.dimensions.length === 0 && set !== 'dimensions') {
            const rows = exporter.query(next);
            const defaults = adapterLib.getAll().filter(a => a.defaults).flatMap(a => a.query(rows));
            next = { ...next, dimensions: defaults.map(x => x.createInstance()) };
        }

        return next;
    };

export const ExportConfigurationView: LFC<{ config: Partial<ExportConfiguration>, onNext: VoidCallback1<ExportConfiguration> }> = (props) => {

    const board = useResolve(EditorSession).get(BoardInfo);
    const [_lastConfig, setLastConfig] = useSessionStorage<SerializedExportConfiguration>(`deqo/board/${board.id}/exportConfig`);

    const sourceLib = useResolve(ExportSourceLibrary);
    const adapters = useResolve(ExportAdapterLibrary);
    const exporter = useResolve(ExportService);

    const [config, dispatch] = useReducer(configReducer(sourceLib, adapters, exporter), ExportConfiguration.empty);

    const AddColumnMenu = useMemo(() => {
        const rows = exporter.query(config);
        const opts = resolveDimensionOptions(rows, adapters.getAll(), config);
        return opts.length ? createAddColumnMenu(opts) : null;
    }, [config]);

    // Load initial state
    useEffectOnce(() => {
        const initial = { ...ExportConfiguration.empty, ...props.config ?? {} };
        dispatch({ set: '*', to: initial });
    });

    // Save state to session store
    useEffect(() => setLastConfig(exporter.serializeConfig(config)), [config]);

    const onNext = useCallback(() => props.onNext(config), [props, config]);

    return <BlockFrame>
        <Block>
            <Title>From</Title>
            <SourcePicker config={config} onPick={to => dispatch({ set: 'source', to })} />
            <Title>Export All</Title>
            <TypePicker config={config} onPick={to => dispatch({ set: 'type', to })} />
        </Block>
        <Block>
            <Title>With Columns</Title>
            <PillEditor
                items={config.dimensions}
                onUpdate={to => dispatch({ set: 'dimensions', to })}
                config={{ elementMenu: AddColumnMenu as any, elementEditor: ColumnEditor }}
            />
        </Block>
        <Block grow>
            <XScrollbars style={{ 'height': '100%' }}>
                <ExportPreview {...config} />
            </XScrollbars>
        </Block>
        <Popup.Actions>
            <Button variant="primary" onAction={onNext}>Next</Button>
        </Popup.Actions>
    </BlockFrame>;
};
